import { useMemo } from "react";
import { queryKeys } from "@/constants/queryKeys";
import { useCustomerScopedQuery } from "@/hooks/useCustomerScopedQuery";
import { services } from "@/services/registry";
function matchesFilter(keyProductCode, keyId, filters) {
    if (filters.productCode && keyProductCode !== filters.productCode) {
        return false;
    }
    if (filters.apiKeyId && keyId !== filters.apiKeyId) {
        return false;
    }
    return true;
}
/**
 * Single-request dashboard hook with client-side product/key filtering.
 *
 * Always fetches the full 30-day window. Shorter time ranges and
 * product/key filters are derived client-side — no re-fetch needed.
 */ export function useDashboardData(filters = {}) {
    const { data, isLoading, error } = useCustomerScopedQuery({
        queryKey: queryKeys.dashboard.data(30),
        queryFn: ({ customerId })=>services.dashboardService.getDashboardData(30, customerId),
        staleTime: 5 * 60 * 1000,
        refetchInterval: 30 * 60 * 1000
    });
    const hasFilter = !!filters.productCode || !!filters.apiKeyId;
    // ── Filtered top keys ──────────────────────────────────────────────
    const topKeys = useMemo(()=>{
        const all = data?.topKeys ?? [];
        if (!hasFilter) return all;
        return all.filter((k)=>matchesFilter(k.productCode, k.apiKeyId, filters));
    }, [
        data?.topKeys,
        filters.productCode,
        filters.apiKeyId,
        hasFilter
    ]);
    // ── Filtered stat cards ────────────────────────────────────────────
    const stats = useMemo(()=>{
        if (!data) {
            return {
                totalEntropyServed: 0,
                totalEntropyUnit: "bytes",
                activeApiKeyCount: 0,
                quantumSecurityGrade: "99.7%",
                geographicFootprint: {
                    countries: 0,
                    continents: 0
                }
            };
        }
        if (!hasFilter) {
            return {
                totalEntropyServed: data.statCards.totalEntropyServed,
                totalEntropyUnit: data.statCards.usageUnit,
                activeApiKeyCount: data.statCards.activeApiKeyCount,
                quantumSecurityGrade: "99.7%",
                geographicFootprint: data.statCards.geoFootprint
            };
        }
        // Re-derive totals from filtered topKeys
        const totalEntropyServed = topKeys.reduce((sum, k)=>sum + k.totalUsageValue, 0);
        const activeApiKeyCount = topKeys.length;
        return {
            totalEntropyServed,
            totalEntropyUnit: data.statCards.usageUnit,
            activeApiKeyCount,
            quantumSecurityGrade: "99.7%",
            // Geo footprint isn't per-key, keep the aggregate
            geographicFootprint: data.statCards.geoFootprint
        };
    }, [
        data,
        topKeys,
        hasFilter
    ]);
    // ── Filtered 24h comparison ────────────────────────────────────────
    const usageComparison24h = useMemo(()=>{
        const raw = data?.usageComparison24h;
        if (!raw) {
            return {
                todayBytes: 0,
                yesterdayBytes: 0,
                deltaPct: 0,
                deltaBytes: 0,
                trending: "flat",
                comparedAtHourUtc: 0,
                avgPerHourToday: 0,
                avgPerHourYesterday: 0,
                peakDelta: null,
                hourlyByKey: []
            };
        }
        if (!hasFilter) return raw;
        // Recompute from per-key hourly data
        const todayByHour = new Array(24).fill(0);
        const yesterdayByHour = new Array(24).fill(0);
        for (const entry of raw.hourlyByKey){
            if (!matchesFilter(entry.productCode, entry.apiKeyId, filters)) {
                continue;
            }
            for(let h = 0; h < 24; h++){
                todayByHour[h] += entry.todayByHour[h] ?? 0;
                yesterdayByHour[h] += entry.yesterdayByHour[h] ?? 0;
            }
        }
        const currentHourUtc = raw.comparedAtHourUtc;
        let todayBytes = 0;
        let yesterdayBytes = 0;
        for(let h = 0; h <= currentHourUtc; h++){
            todayBytes += todayByHour[h];
            yesterdayBytes += yesterdayByHour[h];
        }
        const deltaBytes = todayBytes - yesterdayBytes;
        const deltaPct = yesterdayBytes > 0 ? deltaBytes / yesterdayBytes * 100 : 0;
        const trending = deltaBytes > 0 ? "up" : deltaBytes < 0 ? "down" : "flat";
        const elapsedHours = currentHourUtc + 1;
        const avgPerHourToday = elapsedHours > 0 ? todayBytes / elapsedHours : 0;
        const yesterdayTotal = yesterdayByHour.reduce((s, v)=>s + v, 0);
        const avgPerHourYesterday = yesterdayTotal / 24;
        let peakDelta = null;
        for(let h = 0; h <= currentHourUtc; h++){
            const diff = todayByHour[h] - yesterdayByHour[h];
            if (peakDelta === null || Math.abs(diff) > Math.abs(peakDelta.bytes)) {
                peakDelta = {
                    hour: h,
                    bytes: diff
                };
            }
        }
        return {
            todayBytes,
            yesterdayBytes,
            deltaPct,
            deltaBytes,
            trending,
            comparedAtHourUtc: currentHourUtc,
            avgPerHourToday,
            avgPerHourYesterday,
            peakDelta,
            hourlyByKey: raw.hourlyByKey
        };
    }, [
        data?.usageComparison24h,
        filters.productCode,
        filters.apiKeyId,
        hasFilter
    ]);
    // ── Filtered consumption chart ─────────────────────────────────────
    const consumptionChart = useMemo(()=>{
        const raw = data?.consumptionChart;
        if (!raw) {
            return {
                keys: [],
                totalKeyCount: 0,
                chartDays: 30
            };
        }
        if (!hasFilter) return raw;
        const filtered = raw.keys.filter((k)=>matchesFilter(k.productCode, k.apiKeyId, filters));
        return {
            keys: filtered,
            totalKeyCount: filtered.length,
            chartDays: raw.chartDays
        };
    }, [
        data?.consumptionChart,
        filters.productCode,
        filters.apiKeyId,
        hasFilter
    ]);
    return {
        stats,
        topKeys,
        usageComparison24h,
        consumptionChart,
        raw: data,
        isLoading,
        error: error
    };
}
